import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { EditContactDialog } from "components/Contacts/EditContactDialog";
import { IssueCount } from "components/IssueCount";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { getFullName } from "encamp-shared/src/utils/name";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import { ContactsQuery, PhoneType } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useMemo, useState } from "react";
import { DeleteContactDialog } from "./DeleteContactDialog";
import { StaffBulkActionsMenu } from "./StaffBulkActionsMenu";
import { CommandButton } from "components/CommandButton";
import { MultiLineCell } from "components/MultiLineCell";

function printPhoneType(type: PhoneType) {
  switch (type) {
    case PhoneType.Hrs_24:
      return "24-hour";
    default:
      return prettyPrintEnumValue(type);
  }
}

export type ContactGridProps = {
  tenantId: string;
  commandButtons?: React.ReactNode[];
};

export type Row = ContactsQuery["people"]["items"][number];

export function ContactGrid({ tenantId, commandButtons }: ContactGridProps) {
  const theme = useTheme();
  const { isStaff } = useCurrentUser();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [dialogState, setDialogState] = useState<"edit" | "delete" | null>(
    null
  );
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);

  const columns: GridColDef<Row>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 0.2,
        renderCell: ({ row }) => {
          return (
            <Typography variant="body2" noWrap>
              {getFullName(row)}
            </Typography>
          );
        },
      },
      {
        field: "phone",
        headerName: "Phone",
        flex: 0.2,
        sortable: false,
        renderCell: (params) => (
          <MultiLineCell
            lines={
              params.row.phones?.map(
                (p) => `${p.number} (${printPhoneType(p.type)})`
              ) ?? []
            }
          />
        ),
      },
      {
        field: "email",
        headerName: "Email",
        flex: 0.2,
        renderCell: ({ row }) => {
          return (
            <Typography variant="body2" noWrap>
              {row.email}
            </Typography>
          );
        },
      },
      {
        field: "numFacilities",
        headerName: "Facilities",
        flex: 0.1,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => {
          return (
            <Typography variant="body2" noWrap>
              {row.facilityContacts?.length || ""}
            </Typography>
          );
        },
      },
      {
        field: "issues",
        headerName: "Issues",
        sortable: false,
        flex: 0.1,
        renderCell: ({ row }) => {
          // We aggregate all of the facility contact issues onto the `person`
          const personIssues = row.issues?.length ?? 0;
          return <IssueCount issueCount={personIssues} />;
        },
      },
      {
        field: "actions",
        type: "actions",
        flex: 0.1,
        getActions: (params) => {
          const actions = [
            <Tooltip title="Edit contact" key="edit">
              <GridActionsCellItem
                onClick={() => {
                  setSelectedPersonId(params.row.id);
                  setDialogState("edit");
                }}
                label="Edit contact"
                icon={<Edit />}
              />
            </Tooltip>,
          ];

          // Only add delete button if the contact doesn't have a user account
          if (!params.row.user?.id) {
            actions.push(
              <Tooltip title="Delete contact" key="delete">
                <GridActionsCellItem
                  onClick={() => {
                    setSelectedPersonId(params.row.id);
                    setDialogState("delete");
                  }}
                  label="Delete contact"
                  icon={<Delete />}
                />
              </Tooltip>
            );
          } else {
            actions.push(
              <Tooltip
                title="Cannot delete contact associated to user."
                key="cannot-delete"
              >
                <span>
                  <GridActionsCellItem
                    label="Cannot delete user"
                    icon={<Delete />}
                    disabled
                  />
                </span>
              </Tooltip>
            );
          }

          return actions;
        },
      },
    ],
    [theme]
  );

  const peopleQuery = gql(/* GraphQL */ `
    query Contacts(
      $search: String
      $page: Int
      $pageSize: Int
      $sort: [SortModel!]
    ) {
      people(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
        items {
          id
          first
          last
          email
          user {
            id
          }
          phones {
            type
            number
          }
          facilityContacts {
            id
            reportingRoles
            facility {
              id
              name
              streetAddress1
              streetAddress2
              city
              state
              zip
              country
            }
          }
          issues {
            message
          }
        }
        count
      }
    }
  `);

  const buttons = useMemo(() => {
    const btns = [
      ...(commandButtons ?? []),
      <CommandButton
        key="add"
        variant="contained"
        onClick={() => {
          setSelectedPersonId(null);
          setDialogState("edit");
        }}
      >
        Add Contact
      </CommandButton>,
    ];

    if (isStaff) {
      btns.push(
        <StaffBulkActionsMenu key="staff" contactIds={selectedContactIds} />
      );
    }

    return btns;
  }, [commandButtons, isStaff, selectedContactIds]);

  return (
    <>
      <OmnisearchDataGrid
        persistenceKey="contact-grid"
        isURLDriven
        columns={columns}
        dataQuery={peopleQuery}
        initialSortModel={[{ field: "name", sort: "asc" }]}
        defaultSearch={"tenantId:" + tenantId}
        commandButtons={buttons}
        excludeFilterColumns={["phone", "numFacilities", "issues"]}
        additionalFilterColumns={[
          {
            key: "facility",
            header: "Facility Name",
            filterKeyType: "facility",
          },
        ]}
        noDataMessage="Organization has no contacts."
        getItems={(data) => data.people.items}
        getCount={(data) => data.people.count}
        rowHeight={"auto"}
        onRowClick={(props) => {
          setSelectedPersonId(props.row.id);
          setDialogState("edit");
        }}
        onSelectedIdsChanged={
          isStaff ? (ids) => setSelectedContactIds(ids as string[]) : undefined
        }
        isRowSelectable={(params) => {
          return !!params.row && !params.row.user?.id;
        }}
        disableRowSelectionOnClick={true}
        columnVisibilityModel={{
          ...(isMobile && {
            phone: false,
            numFacilities: false,
            issues: false,
          }),
        }}
      />

      {dialogState === "delete" && (
        <DeleteContactDialog
          open
          onClose={() => {
            setSelectedPersonId(null);
            setDialogState(null);
          }}
          personId={selectedPersonId ?? ""}
        />
      )}

      {dialogState === "edit" && (
        <EditContactDialog
          open
          onClose={() => {
            setSelectedPersonId(null);
            setDialogState(null);
          }}
          onSave={async (person) => {
            if (!selectedPersonId && person?.id) {
              setSelectedPersonId(person?.id ?? undefined);
            } else {
              setSelectedPersonId(null);
              setDialogState(null);
            }
          }}
          personId={selectedPersonId ?? undefined}
          refetchQueries={["Contacts"]}
        />
      )}
    </>
  );
}
